import { useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, CheckSquare } from 'lucide-react';

interface Task {
  id: string;
  title: string;
  project: string;
  status: 'Backlog' | 'Not Started' | 'In Progress' | 'Quality Check' | 'Done';
  timeLogged: string;
  dueDate?: string;
  priority: 'high' | 'medium' | 'low';
}

interface RecentTasksProps {
  tasks?: Task[];
  onTaskClick?: (task: Task) => void;
}

// Sample data - in production would come from context
const sampleTasks: Task[] = [
  {
    id: '1',
    title: 'Design Homepage Banner',
    project: 'Website Redesign',
    status: 'In Progress',
    timeLogged: '3h 45m',
    dueDate: '2024-01-15',
    priority: 'high',
  },
  {
    id: '2',
    title: 'Mobile App UI Components',
    project: 'Mobile App',
    status: 'In Progress',
    timeLogged: '5h 30m',
    dueDate: '2024-01-16',
    priority: 'medium',
  },
  {
    id: '3',
    title: 'Content Strategy Document',
    project: 'Brand Strategy',
    status: 'Quality Check',
    timeLogged: '2h 15m',
    dueDate: '2024-01-14',
    priority: 'low',
  },
  {
    id: '4',
    title: 'User Research Analysis',
    project: 'UX Research',
    status: 'Not Started',
    timeLogged: '0h 0m',
    dueDate: '2024-01-18',
    priority: 'medium',
  },
];

const ITEMS_PER_PAGE = 3;

export function RecentTasks({ tasks = sampleTasks, onTaskClick }: RecentTasksProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(tasks.length / ITEMS_PER_PAGE);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentTasks = tasks.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'Done':
        return 'bg-[#27AE60] text-white';
      case 'In Progress':
        return 'bg-[#4A90E2] text-white';
      case 'Quality Check':
        return 'bg-[#F5A623] text-white';
      case 'Not Started':
        return 'bg-[#808080] text-white';
      case 'Backlog':
        return 'bg-[#b3b3b3] text-white';
      default:
        return 'bg-[#f6f6f6] text-[#808080]';
    }
  };

  const getPriorityIndicator = (priority: Task['priority']) => {
    switch (priority) {
      case 'high':
        return 'border-l-[#e30840]';
      case 'medium':
        return 'border-l-[#F5A623]';
      case 'low':
        return 'border-l-[#27AE60]';
      default:
        return 'border-l-[#808080]';
    }
  };

  return (
    <div className="bg-white rounded-md shadow-[0px_2px_15px_-3px_rgba(0,0,0,0.07),0px_10px_20px_-2px_rgba(0,0,0,0.04)] p-6 h-[370px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-[12px] text-[#808080] uppercase tracking-wide">Recent Tasks</p>
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrevPage}
            disabled={currentPage === 1}
            className="w-6 h-6 flex items-center justify-center rounded-md border border-[#e6e6e6] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#f6f6f6] transition-colors"
          >
            <ChevronLeft className="w-3 h-3 text-[#808080]" />
          </button>
          <span className="text-[11px] text-[#808080]">
            {currentPage} / {totalPages || 1}
          </span>
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages || totalPages === 0}
            className="w-6 h-6 flex items-center justify-center rounded-md border border-[#e6e6e6] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#f6f6f6] transition-colors"
          >
            <ChevronRight className="w-3 h-3 text-[#808080]" />
          </button>
        </div>
      </div>

      {/* Tasks List */}
      <div className="flex-1 overflow-hidden space-y-3">
        {currentTasks.length > 0 ? (
          currentTasks.map((task) => (
            <div
              key={task.id}
              onClick={() => onTaskClick?.(task)}
              className={`p-4 bg-[#fafafa] rounded-md border-l-4 ${getPriorityIndicator(task.priority)} cursor-pointer hover:bg-[#f5f5f5] transition-colors`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-[#262626] truncate">
                    {task.title}
                  </p>
                  <p className="text-[11px] text-[#808080] mt-1">{task.project}</p>
                </div>
                <span
                  className={`text-[10px] px-2 py-1 rounded-full whitespace-nowrap ${getStatusColor(task.status)}`}
                >
                  {task.status}
                </span>
              </div>

              <div className="flex items-center gap-4 mt-3">
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3 text-[#808080]" />
                  <span className="text-[10px] text-[#808080]">{task.timeLogged}</span>
                </div>
                {task.dueDate && (
                  <div className="flex items-center gap-1">
                    <CheckSquare className="w-3 h-3 text-[#808080]" />
                    <span className="text-[10px] text-[#808080]">
                      Due: {new Date(task.dueDate).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center h-full text-[12px] text-[#808080]">
            No tasks found
          </div>
        )}
      </div>
    </div>
  );
}
